import { getStore } from "app/store/store"
import { getDifference } from "app/tools/common"
import { getCompactFormattedNumber } from "app/tools/number"
import { IEnvironment, IEnvironmentsForecast } from "environments/model"
import { environmentsRPO } from "environments/repository"
import { IForm, IFormDataLoader, IFormEnvironmentsRow } from "form/model"
import { state } from "form/state"
import { formActionCreator } from "form/store/actions"
import { getForm } from "./common"

export const environmentForecastLoader: IFormDataLoader = async (form: IForm, onFinish: (error?: string) => any) => {
  try {
    form.context.environment.initialForecast = await environmentsRPO.getEnvironmentForecast(form.tempCampaignId)
    onFinish()
  } catch (error) {
    console.error(error)
    onFinish(`Environment forecast data-loader error ${String(error)}`)
  }
}

// @recursive
export function refreshEnvironmentList(): void {
  if (state.environmentsForecastIsLoading) {
    state.environmentsForecastRefreshAgain = true
    return
  } else {
    state.environmentsForecastIsLoading = true
  }
  const form = getForm()
  getStore().dispatch(formActionCreator.startLoadingEnvironmentsForecast(form.context.appContext))
  const finish = () => {
    state.environmentsForecastIsLoading = false
    if (state.environmentsForecastRefreshAgain) {
      state.environmentsForecastRefreshAgain = false
      // @recursivity
      refreshEnvironmentList()
    }
  }
  environmentsRPO
    .getEnvironmentForecast(form.tempCampaignId)
    .then(newForecast => {
      getStore().dispatch(formActionCreator.setEnvironmentsForecast(form.context.appContext, newForecast))
      finish()
    })
    .catch(error => {
      console.error(error)
      getStore().dispatch(formActionCreator.stopLoadingEnvironmentsForecast(form.context.appContext))
      finish()
    })
}

export function getEnvironmentRows(form: IForm, environments: IEnvironment[]): IFormEnvironmentsRow[] {
  const {
    context: {
      environment: { initialForecast, currentForecast }
    }
  } = form
  return environments.map(e => getEnvironmentRow(e, initialForecast, currentForecast))
}

export function getTreeEnvironmentRows(form: IForm, tree: IEnvironment[]): IFormEnvironmentsRow[] {
  const rows: IFormEnvironmentsRow[] = []
  for (const treeElement of tree) {
    addTreeRows(form, rows, treeElement)
  }
  return rows
}

// return true if one of the children is selected
function addTreeRows(form: IForm, rows: IFormEnvironmentsRow[], treeElement: IEnvironment): boolean {
  const selectedIds = form.screens.environmentsIds
  const selfRow = getEnvironmentRow(
    treeElement,
    form.context.environment.initialForecast,
    form.context.environment.currentForecast
  )
  rows.push(selfRow)
  let hasSomeChildrenSelected: boolean = false
  if (treeElement.children.length > 0) {
    if (form.screens.environmentExpandedIds.includes(treeElement.id)) {
      for (const child of treeElement.children) {
        addTreeRows(form, rows, child)
      }
    }
    if (!hasSomeChildrenSelected && hasChildrenSelected(selectedIds, treeElement)) {
      hasSomeChildrenSelected = true
    }
    if (!selectedIds.includes(treeElement.id) && hasSomeChildrenSelected) {
      selfRow.isIndeterminate = true
    }
  }
  return hasSomeChildrenSelected
}

// @recursive
function hasChildrenSelected(selectedIds: number[], treeElement: IEnvironment): boolean {
  for (const child of treeElement.children) {
    // @recursivity
    if (selectedIds.includes(child.id) || hasChildrenSelected(selectedIds, child)) {
      return true
    }
  }
  return false
}

function getEnvironmentRow(
  environment: IEnvironment,
  initialForecast: IEnvironmentsForecast | null,
  currentForecast: IEnvironmentsForecast
): IFormEnvironmentsRow {
  const environmentInitialForecast = initialForecast ? initialForecast[environment.id] : undefined
  const environmentCurrentForecast = currentForecast[environment.id]
  const impression = environmentCurrentForecast ? environmentCurrentForecast.impression : 0
  const impressionRatio = environmentCurrentForecast
    ? `${getCompactFormattedNumber(environmentCurrentForecast.impression)}/${getCompactFormattedNumber(
        environmentCurrentForecast.impressionTotal
      )}`
    : "-"
  const impressionPercent = environmentCurrentForecast ? environmentCurrentForecast.impressionPercent : -1
  const impressionDiff = getDifference(
    environmentInitialForecast ? environmentInitialForecast.impressionPercent : undefined,
    environmentCurrentForecast ? environmentCurrentForecast.impressionPercent : undefined
  )
  const screens = environmentCurrentForecast ? environmentCurrentForecast.screen : 0
  const screensRatio = environmentCurrentForecast
    ? `${getCompactFormattedNumber(environmentCurrentForecast.screen)}/${getCompactFormattedNumber(
        environmentCurrentForecast.screenTotal
      )}`
    : "-"
  const screensPercent = environmentCurrentForecast ? environmentCurrentForecast.screenPercent : -1
  const screensDiff = getDifference(
    environmentInitialForecast ? environmentInitialForecast.screenPercent : undefined,
    environmentCurrentForecast ? environmentCurrentForecast.screenPercent : undefined
  )
  return {
    id: environment.id,
    name: environment.name,
    impression,
    impressionRatio,
    impressionPercent,
    impressionDiff,
    screens,
    screensRatio,
    screensPercent,
    screensDiff,
    environment,
    isIndeterminate: false
  }
}
